import { Form, Input, InputNumber, Modal, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import IWarehouse from '~Api/Warehouse/IWarehouse';
import WarehouseTypeEnum from '~Api/Warehouse/WarehouseTypeEnum';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import { warehouseAdjustPrincipalAction } from '~Warehouses/actions';
import { warehouseSelector } from '~Warehouses/selectors';

interface IState {
    aNotes: number;
    amount: number;
    capacityLimit: number,
    description: string;
    prefund: number;
    principalDraw: number;
    sellerNotes: number;
    transactionDateTime: string;
    xNotes: number;
}

interface IProps {
    isOpen: boolean;
    onCancel: () => void;
    warehouseUuid: string;
}

interface IPropsSelector {
    warehouse: IWarehouse;
}

interface IPropsDispatch {
    adjustPrincipal: (
        aNotes: number,
        amount: number,
        capacityLimit: number,
        principalDraw: number,
        sellerNotes: number,
        description: string,
        transactionTime: string,
        xNotes: number,
        prefund: number,
    ) => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class PrincipalModal extends React.Component<Props, IState> {
    public state: IState = {
        aNotes: null,
        amount: null,
        capacityLimit: null,
        description: null,
        prefund: null,
        principalDraw: null,
        sellerNotes: null,
        transactionDateTime: null,
        xNotes: null,
    };

    constructor(props: Props) {
        super(props);

        this.onChangeAnotes = this.onChangeAnotes.bind(this);
        this.onChangeAmount = this.onChangeAmount.bind(this);
        this.onChangeCapacityLimit = this.onChangeCapacityLimit.bind(this);
        this.onChangePrefund = this.onChangePrefund.bind(this);
        this.onChangePrincipalDraw = this.onChangePrincipalDraw.bind(this);
        this.onChangeSellerNotes = this.onChangeSellerNotes.bind(this);
        this.onChangeDescription = this.onChangeDescription.bind(this);
        this.onChangeTransactionDate = this.onChangeTransactionDate.bind(this);
        this.onChangeTransactionTime = this.onChangeTransactionTime.bind(this);
        this.onChangeXnotes = this.onChangeXnotes.bind(this);

        this.onClickCancel = this.onClickCancel.bind(this);
        this.onClickOk = this.onClickOk.bind(this);
    }

    public render(): JSX.Element {
        const { isOpen, warehouse } = this.props;
        const {
            aNotes,
            amount,
            capacityLimit,
            description,
            prefund,
            principalDraw,
            sellerNotes,
            transactionDateTime,
            xNotes,
        } = this.state;

        const transactionDateTimeDayJs: Dayjs = transactionDateTime ? dayjs(transactionDateTime) : null;

        const classAnoteBlock: JSX.Element = [WarehouseTypeEnum.Fnom, WarehouseTypeEnum.Fwt2].includes(warehouse.type) && (
            <Form.Item
                label={WarehouseTypeEnum.Fnom === warehouse.type ? 'Principal' : 'Class A Notes'}
                className='a-notes'
            >
                <InputNumber addonBefore='$' onChange={this.onChangeAnotes} value={aNotes} />
            </Form.Item>
        );

        const classXnoteBlock: JSX.Element = WarehouseTypeEnum.Fwt2 === warehouse.type && (
            <Form.Item label='Class X Notes' className='x-notes'>
                <InputNumber addonBefore='$' onChange={this.onChangeXnotes} value={xNotes} />
            </Form.Item>
        );

        const prefundBlock: JSX.Element = WarehouseTypeEnum.Fwt1 === warehouse.type && (
            <Form.Item label='Prefund' className='prefund'>
                <InputNumber addonBefore='$' onChange={this.onChangePrefund} value={prefund} />
            </Form.Item>
        );

        const principalDrawBlock: JSX.Element = [WarehouseTypeEnum.Fwt1, WarehouseTypeEnum.Fwt2].includes(warehouse.type) && (
            <Form.Item label='Principal Draw' className='principal-draw'>
                <InputNumber addonBefore='$' onChange={this.onChangePrincipalDraw} value={principalDraw} />
            </Form.Item>
        );

        const sellerNotesBlock: JSX.Element = [WarehouseTypeEnum.Fwt1, WarehouseTypeEnum.Fwt2].includes(warehouse.type) && (
            <Form.Item label='Seller Notes' className='seller-notes'>
                <InputNumber addonBefore='$' onChange={this.onChangeSellerNotes} value={sellerNotes} />
            </Form.Item>
        );

        return (
            <Modal
                okText='Record Adjustment'
                onCancel={this.onClickCancel}
                onOk={this.onClickOk}
                open={isOpen}
                title='Adjust Principal'
                wrapClassName='warehouse-adjust-principal-modal'
            >
                <Form.Item label='Transaction Date' className='transaction-date'>
                    <Space>
                        <DatePicker className='transaction-pick-date' onChange={this.onChangeTransactionDate} value={transactionDateTimeDayJs} format='DD/MM/YYYY' />
                        <TimePicker className='transaction-pick-time' onChange={this.onChangeTransactionTime} value={transactionDateTimeDayJs} format='H:mm' />
                    </Space>
                </Form.Item>
                {classAnoteBlock}
                <Form.Item label={WarehouseTypeEnum.Fnom === warehouse.type ? 'First Loss' : 'Class B Notes'} className='amount'>
                    <InputNumber addonBefore='$' onChange={this.onChangeAmount} value={amount} />
                </Form.Item>
                {classXnoteBlock}
                <Form.Item label='Capacity Limit' className='capacity-limit'>
                    <InputNumber addonBefore='$' onChange={this.onChangeCapacityLimit} value={capacityLimit} />
                </Form.Item>
                {principalDrawBlock}
                {sellerNotesBlock}
                {prefundBlock}
                <Form.Item label='Description' className='description'>
                    <Input onChange={this.onChangeDescription} value={description} />
                </Form.Item>
            </Modal>
        );
    }

    private onChangeAnotes(value: number) {
        this.setState({
            aNotes: value,
        });
    }

    private onChangeAmount(value: number) {
        this.setState({
            amount: value,
        });
    }

    private onChangeCapacityLimit(value: number) {
        this.setState({
            capacityLimit: value,
        });
    }

    private onChangePrefund(value: number) {
        this.setState({
            prefund: value,
        });
    }

    private onChangePrincipalDraw(value: number) {
        this.setState({
            principalDraw: value,
        });
    }

    private onChangeSellerNotes(value: number) {
        this.setState({
            sellerNotes: value,
        });
    }

    private onChangeDescription(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({
            description: event.target.value,
        });
    }

    private onChangeTransactionDate(date: Dayjs) {
        this.setState({
            transactionDateTime: date ? date.format('YYYY-MM-DD') : null,
        });
    }

    private onChangeTransactionTime(time: Dayjs) {
        const { transactionDateTime } = this.state;

        this.setState({
            transactionDateTime: dayjs(`${dayjs(transactionDateTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format(),
        });
    }

    private onChangeXnotes(value: number) {
        this.setState({
            xNotes: value,
        });
    }

    private onClickCancel() {
        this.setState({
            aNotes: null,
            amount: null,
            capacityLimit: null,
            description: null,
            prefund: null,
            principalDraw: null,
            sellerNotes: null,
            transactionDateTime: dayjs().format(),
            xNotes: null,
        });
        this.props.onCancel();
    }

    private onClickOk() {
        const {
            aNotes,
            amount,
            capacityLimit,
            description,
            prefund,
            principalDraw,
            sellerNotes,
            transactionDateTime,
            xNotes,
        } = this.state;

        const transactionDateTimeDayJs: Dayjs = transactionDateTime ? dayjs(transactionDateTime) : null;
        if (!transactionDateTimeDayJs) {
            return;
        }

        this.props.adjustPrincipal(
            aNotes,
            amount,
            capacityLimit,
            principalDraw,
            sellerNotes,
            description,
            transactionDateTimeDayJs.format('YYYY-MM-DDTHH:mm:ssZ'),
            xNotes,
            prefund,
        );
        this.onClickCancel();
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        warehouse: warehouseSelector(state, ownProps.warehouseUuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        adjustPrincipal: (
            aNotes: number,
            amount: number,
            capacityLimit: number,
            principalDraw: number,
            sellerNotes: number,
            description: string,
            transactionDateTime: string,
            xNotes: number,
            prefund: number,
        ) => dispatch(warehouseAdjustPrincipalAction(
            ownProps.warehouseUuid,
            aNotes,
            amount,
            capacityLimit,
            principalDraw,
            sellerNotes,
            description,
            transactionDateTime,
            xNotes,
            prefund,
        )),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(PrincipalModal);
